import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { 
  validateImageFile, 
  getImageInfo, 
  resizeImage, 
  formatFileSize,
  createImagePreview 
} from '../../utils/imageUtils';
import './CadastroCurso.css';

const CadastroCurso = () => {
  const [curso, setCurso] = useState({
    title: '',
    description: '',
    imagem: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [imagemPreview, setImagemPreview] = useState(null);
  const [imagemInfo, setImagemInfo] = useState(null);
  const [processandoImagem, setProcessandoImagem] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurso(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar mensagens quando o usuário começar a digitar
    if (error) setError(null);
  };

  const handleImagemChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setProcessandoImagem(true);
      setError(null);

      // Validar arquivo
      const validation = validateImageFile(file, 10); // Máx 10MB
      if (!validation.valid) {
        setError(validation.error);
        return;
      }

      // Obter informações da imagem original
      const imageInfo = await getImageInfo(file);
      setImagemInfo(imageInfo);

      // Redimensionar imagem para 640px de largura mantendo proporção
      const resizedBase64 = await resizeImage(file, 640, 0.85);
      
      // Criar preview
      const preview = await createImagePreview(file);
      
      setCurso(prev => ({
        ...prev,
        imagem: resizedBase64
      }));
      setImagemPreview(preview);

    } catch (err) {

      setError('Erro ao processar a imagem. Tente novamente.');
    } finally {
      setProcessandoImagem(false);
    }
  };

  const removerImagem = () => {
    setCurso(prev => ({
      ...prev,
      imagem: null
    }));
    setImagemPreview(null);
    setImagemInfo(null);
    
    // Limpar input file
    const fileInput = document.getElementById('imagem');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!curso.title.trim() || !curso.description.trim()) {
      setError('Todos os campos são obrigatórios');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Criar curso primeiro sem a imagem
      const cursoData = {
        title: curso.title,
        description: curso.description
      };
      
      const response = await apiService.cursos.create(cursoData);
      const cursoId = response.data.id;
      
      // Se há imagem, fazer upload separadamente
      if (curso.imagem) {
        try {
          const uploadResponse = await apiService.cursos.uploadImagem(cursoId, {
            imagem: curso.imagem,
            nome_original: imagemInfo ? imagemInfo.name : 'imagem.jpg'
          });
          

          
          // Exibir informações do processamento
          if (uploadResponse.data.processamento) {
            const proc = uploadResponse.data.processamento;

          }
          
        } catch (imgError) {

          // Curso foi criado mas imagem falhou - avisar o usuário
          setError('Curso criado, mas houve erro no upload da imagem. Você pode adicionar a imagem depois.');
        }
      }
      
      setSuccess(true);
      setTimeout(() => {
        navigate(`/admin/cursos/${cursoId}/modulos`);
      }, 2000);
      
    } catch (err) {

      if (err.response?.status === 413) {
        setError('Arquivo muito grande. Tente uma imagem menor.');
      } else if (err.response?.status === 422) {
        setError('Dados inválidos. Verifique os campos e tente novamente.');
      } else {
        setError('Erro ao criar curso. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/cursos');
  };

  if (success) {
    return (
      <div className="cadastro-curso-container">
        <div className="success-message">
          <div className="success-icon">✅</div>
          <h2>Curso criado com sucesso!</h2>
          <p>Redirecionando para cadastro de módulos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cadastro-curso-container" data-page="cadastro-curso">
      <div className="cadastro-header">
        <h1>Cadastrar Novo Curso</h1>
        <p>Preencha as informações básicas do curso</p>
      </div>

      <div className="cadastro-content">
        <form onSubmit={handleSubmit} className="curso-form">
          {error && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="title">Título do Curso *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={curso.title}
              onChange={handleChange}
              placeholder="Digite o título do curso"
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Descrição do Curso *</label>
            <textarea
              id="description"
              name="description"
              value={curso.description}
              onChange={handleChange}
              placeholder="Digite uma descrição detalhada do curso"
              className="form-textarea"
              rows={5}
              required
            />
          </div>

          {/* Upload de Imagem */}
          <div className="form-group">
            <label htmlFor="imagem">Imagem do Curso</label>
            <div className="image-upload-container">
              {imagemPreview ? (
                <div className="image-preview">
                  <img src={imagemPreview} alt="Preview do curso" className="preview-image" />
                  <div className="image-info">
                    {imagemInfo && (
                      <div className="image-details">
                        <p><strong>Original:</strong> {imagemInfo.width}x{imagemInfo.height}px ({formatFileSize(imagemInfo.size)})</p>
                        <p><strong>Processada:</strong> Será redimensionada para 640px de largura e convertida para WebP</p>
                      </div>
                    )}
                  </div>
                  <div className="image-actions">
                    <button
                      type="button"
                      onClick={removerImagem}
                      className="btn-remove-image"
                      title="Remover imagem"
                    >
                      🗑️ Remover
                    </button>
                  </div>
                </div>
              ) : (
                <div className="image-upload-area">
                  <input
                    type="file"
                    id="imagem"
                    name="imagem"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleImagemChange}
                    className="file-input"
                    disabled={processandoImagem}
                  />
                  <label htmlFor="imagem" className={`file-input-label ${processandoImagem ? 'processing' : ''}`}>
                    <div className="upload-icon">📸</div>
                    <div className="upload-text">
                      {processandoImagem ? (
                        <>
                          <strong>Processando imagem...</strong>
                          <p>Redimensionando e otimizando</p>
                        </>
                      ) : (
                        <>
                          <strong>Clique para selecionar uma imagem</strong>
                          <p>ou arraste e solte aqui</p>
                          <small>JPEG, PNG, GIF ou WebP - Máx. 10MB</small>
                          <small>Será automaticamente redimensionada para 640px e convertida para WebP</small>
                        </>
                      )}
                    </div>
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={handleCancel}
              className="btn-secondary"
              disabled={loading}
            >
              Cancelar
            </button>
            
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || processandoImagem}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  Criando...
                </>
              ) : (
                <>
                  <span className="btn-icon">💾</span>
                  Criar Curso
                </>
              )}
            </button>
          </div>
        </form>

        <div className="next-steps">
          <h3>Próximos passos:</h3>
          <ol>
            <li>Criar curso com informações básicas</li>
            <li>Adicionar módulos (aulas) ao curso</li>
            <li>Configurar conteúdo dos módulos</li>
            <li>Publicar curso</li>
          </ol>
          
          <div className="image-info-box">
            <h4>📸 Sobre as imagens:</h4>
            <ul>
              <li>Imagens são automaticamente redimensionadas para 640px de largura</li>
              <li>Convertidas para formato WebP para melhor performance</li>
              <li>Proporção original é mantida</li>
              <li>Tamanho máximo: 10MB (original)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastroCurso;
